/**
 * CR-5770: Master Contract create modal
 * Captures the high-level supplier contract award (no parts / no line pricing).
 * Renders inside the supplier-detail Contracts tab; the supplier id is fixed
 * by the caller. CR-7787: the customer defaults to the site in view (else the
 * user's home company), and Customer and Contract Owner come from the
 * contract form-options endpoint, so they offer only what the save accepts.
 */
import { useState, useEffect } from 'react';
import { X, Loader2, Save } from 'lucide-react';
import { purchasingAPI, ContractPrice } from '../../lib/api';
import { apiErrorMessage } from '../../lib/api/errors';
import { useContractFormOptions } from '../../hooks/useContractFormOptions';
import { toast } from 'sonner';
import FileDropZone from '../common/FileDropZone';
import ContractOwnerSelect from './ContractOwnerSelect';

interface MasterContractCreateModalProps {
  supplierId: number;
  supplierName: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (contract: ContractPrice) => void;
}

interface FormState {
  contract_number: string;
  customer: string;
  valid_to: string;
  currency: string;
  auto_renew: boolean;
  renewal_period_years: string;
  notice_period_months: string;
  reminder_days: string;
  risk_category: string;
  br_frequency_months: string;
  owner: string;
  contract_negotiation: boolean;
  notes: string;
}

const EMPTY_FORM: FormState = {
  contract_number: '',
  customer: '',
  valid_to: '',
  currency: 'GBP',
  auto_renew: false,
  renewal_period_years: '',
  notice_period_months: '',
  reminder_days: '',
  risk_category: '',
  br_frequency_months: '',
  owner: '',
  contract_negotiation: false,
  notes: '',
};

export default function MasterContractCreateModal({
  supplierId,
  supplierName,
  isOpen,
  onClose,
  onCreated,
}: MasterContractCreateModalProps) {
  const [saving, setSaving] = useState(false);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const {
    customers, defaultCustomer, customersLoading, customersError,
    owners, ownersLoading, ownersError,
  } = useContractFormOptions({
    isOpen,
    supplierId,
    customerId: form.customer ? Number(form.customer) : null,
  });

  useEffect(() => {
    if (!isOpen) return;
    setForm(EMPTY_FORM);
    setContractFile(null);
  }, [isOpen]);

  // Legacy filed the contract against the user's current company; preselect
  // the nearest equivalent once the options arrive, never over a user's pick.
  useEffect(() => {
    if (isOpen && defaultCustomer) {
      setForm((prev) => (prev.customer ? prev : { ...prev, customer: String(defaultCustomer) }));
    }
  }, [isOpen, defaultCustomer]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!form.contract_number.trim()) {
      toast.error('Contract number is required');
      return;
    }
    if (!form.customer) {
      toast.error('Customer is required');
      return;
    }
    if (!form.valid_to) {
      toast.error('Expiry date is required');
      return;
    }
    if (!form.owner) {
      toast.error('Contract owner is required');
      return;
    }

    try {
      setSaving(true);
      const fd = new FormData();
      // CR-6424: this modal creates supplier RELATIONSHIP contracts — the
      // realm keeps them out of Purchasing > Pricing (pricebooks).
      fd.append('realm', 'relationship');
      fd.append('contract_number', form.contract_number.trim());
      fd.append('supplier', String(supplierId));
      fd.append('customer', form.customer);
      fd.append('valid_to', form.valid_to);
      fd.append('currency', form.currency);
      fd.append('auto_renew', String(form.auto_renew));
      if (form.auto_renew && form.renewal_period_years !== '') {
        fd.append('renewal_period_years', String(form.renewal_period_years));
      }
      if (form.notice_period_months !== '') {
        fd.append('notice_period_months', String(form.notice_period_months));
      }
      if (form.reminder_days !== '') {
        fd.append('reminder_days', String(form.reminder_days));
      }
      if (form.risk_category) {
        fd.append('risk_category', form.risk_category);
      }
      if (form.br_frequency_months !== '') {
        fd.append('br_frequency_months', String(form.br_frequency_months));
      }
      fd.append('owner', form.owner);
      fd.append('contract_negotiation', String(form.contract_negotiation));
      if (form.notes) {
        fd.append('notes', form.notes);
      }
      if (contractFile) {
        fd.append('contract_file', contractFile);
      }

      const res = await purchasingAPI.createContractPriceFormData(fd);
      toast.success('Contract created');
      onCreated(res.data);
    } catch (err: unknown) {
      // The server answers in the Helix error envelope; a field error (owner,
      // contract number) sits under details.fields.
      toast.error(apiErrorMessage(err, 'Failed to create contract'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <div>
            <h2 className="text-lg font-semibold">New Master Contract</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Supplier: <span className="font-medium">{supplierName}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">
                Contract Number<span className="text-red-500"> *</span>
              </label>
              <input
                type="text"
                value={form.contract_number}
                onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. CON-2026-001"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">
                Customer<span className="text-red-500"> *</span>
              </label>
              <select
                value={form.customer}
                // The owner belongs to the customer, so a new customer clears it.
                onChange={(e) => setForm({ ...form, customer: e.target.value, owner: '' })}
                disabled={customersLoading}
                aria-invalid={Boolean(customersError)}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-muted"
              >
                <option value="">{customersLoading ? 'Loading customers…' : '-- Select --'}</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {customersError && (
                <p className="text-xs text-destructive mt-1">
                  {customersError} Close the form and open it again to retry.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">
                Expiry Date<span className="text-red-500"> *</span>
              </label>
              <input
                type="date"
                value={form.valid_to}
                onChange={(e) => setForm({ ...form, valid_to: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Currency</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="GBP">GBP — British Pound</option>
                <option value="USD">USD — US Dollar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="JPY">JPY — Japanese Yen</option>
                <option value="CNY">CNY — Chinese Yuan</option>
                <option value="INR">INR — Indian Rupee</option>
                <option value="AUD">AUD — Australian Dollar</option>
                <option value="CAD">CAD — Canadian Dollar</option>
                <option value="CHF">CHF — Swiss Franc</option>
                <option value="MXN">MXN — Mexican Peso</option>
                <option value="PLN">PLN — Polish Złoty</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Contract File</label>
            <div className="flex items-center gap-2">
              {/* Customer review 11 Sep (drag and drop everywhere). */}
              <FileDropZone
                size="compact"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                hint={contractFile ? <>PDF, DOC, XLS · <span className="font-medium text-foreground">{contractFile.name}</span></> : 'PDF, DOC, XLS'}
                onFiles={(files) => setContractFile(files[0])}
                className="flex-1 min-w-0"
                data-testid="master-contract-file-drop-zone"
              />
              {contractFile && (
                <button
                  type="button"
                  onClick={() => {
                    setContractFile(null);
                  }}
                  className="text-muted-foreground hover:text-foreground p-1"
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Auto-Renew</label>
              <select
                value={form.auto_renew ? 'yes' : 'no'}
                onChange={(e) => {
                  const enabled = e.target.value === 'yes';
                  setForm({
                    ...form,
                    auto_renew: enabled,
                    renewal_period_years: enabled ? form.renewal_period_years : '',
                  });
                }}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">
                Renewal Period (years)
                {!form.auto_renew && (
                  <span className="text-muted-foreground font-normal ml-1">— enable Auto-Renew to edit</span>
                )}
              </label>
              <input
                type="number"
                min="0"
                value={form.auto_renew ? form.renewal_period_years : ''}
                onChange={(e) => setForm({ ...form, renewal_period_years: e.target.value })}
                disabled={!form.auto_renew}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                placeholder={form.auto_renew ? 'e.g. 1' : '—'}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Notice Period (months)</label>
              <input
                type="number"
                min="0"
                value={form.notice_period_months}
                onChange={(e) => setForm({ ...form, notice_period_months: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Reminder (days before expiry)</label>
              <input
                type="number"
                min="0"
                value={form.reminder_days}
                onChange={(e) => setForm({ ...form, reminder_days: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. 30"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Risk Category</label>
              <select
                value={form.risk_category}
                onChange={(e) => setForm({ ...form, risk_category: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">-- Select --</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">BR Frequency (months)</label>
              <input
                type="number"
                min="0"
                value={form.br_frequency_months}
                onChange={(e) => setForm({ ...form, br_frequency_months: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. 6"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Contract Negotiation</label>
              <select
                value={form.contract_negotiation ? 'true' : 'false'}
                onChange={(e) => setForm({ ...form, contract_negotiation: e.target.value === 'true' })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">
                Contract Owner<span className="text-red-500"> *</span>
              </label>
              <ContractOwnerSelect
                value={form.owner}
                onChange={(owner) => setForm({ ...form, owner })}
                customerChosen={Boolean(form.customer)}
                owners={owners}
                loading={ownersLoading}
                error={ownersError}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
              placeholder="Optional notes about the contract"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-background border-t px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Create Contract
          </button>
        </div>
      </div>
    </div>
  );
}
